import { normalizeMaterialMode, type MaterialMode } from '../domain/materialMode';

export interface ClientPdfSummaryRow {
  label: string;
  value: string;
}

export interface BuildClientPdfSummaryRowsInput {
  laborNet: number;
  materialsNet: number;
  netTotal: number;
  vatLabel?: string;
  vatRate: number;
  vatValue: number;
  grossTotal: number;
  materialMode: MaterialMode;
}

export interface ClientPdfSummaryRowsResult {
  summaryRows: ClientPdfSummaryRow[];
  materialNote?: string;
}

export function buildClientPdfSummaryRows(input: BuildClientPdfSummaryRowsInput): ClientPdfSummaryRowsResult {
  const normalizedMaterialMode = normalizeMaterialMode(input.materialMode);
  const summaryRows: ClientPdfSummaryRow[] = [
    { label: 'Robocizna netto', value: formatPLN(input.laborNet) },
  ];

  const hasExplicitMaterials = input.materialsNet > 0 &&
    (normalizedMaterialMode === 'CLIENT_MATERIALS' || normalizedMaterialMode === 'INFO_MATERIALS');
  if (input.materialsNet > 0) {
    summaryRows.push({
      label: hasExplicitMaterials ? 'Materiały dodane do oferty netto' : 'Materiały netto',
      value: formatPLN(input.materialsNet),
    });
  }

  summaryRows.push({ label: 'Razem netto', value: formatPLN(input.netTotal) });

  const shouldShowVatRow =
    input.vatLabel === 'VAT: do potwierdzenia' || input.vatRate > 0;
  if (shouldShowVatRow) {
    summaryRows.push({
      label: input.vatLabel ?? `VAT ${String(input.vatRate)}%`,
      value: input.vatLabel === 'VAT: do potwierdzenia' ? 'do potwierdzenia' : formatPLN(input.vatValue),
    });
  }

  summaryRows.push({
    label: input.vatRate > 0 ? 'Cena brutto dla klienta' : 'Cena dla klienta',
    value: formatPLN(input.grossTotal),
  });

  return {
    summaryRows,
    materialNote: hasExplicitMaterials
      ? `${normalizedMaterialMode === 'CLIENT_MATERIALS' ? 'Materiały po stronie klienta.' : 'Materiały informacyjnie.'} Cena obejmuje również materiały osobno dodane do oferty i wymienione w jej pozycjach.`
      : getMaterialNote(normalizedMaterialMode),
  };
}

function getMaterialNote(mode: ReturnType<typeof normalizeMaterialMode>): string | undefined {
  if (mode === 'CLIENT_MATERIALS') {
    return 'Materiały po stronie klienta. Cena obejmuje robociznę, bez doliczania materiałów.';
  }

  if (mode === 'INFO_MATERIALS') {
    return 'Szacunkowy koszt materiałów informacyjnie. Materiały nie są wliczone w cenę usługi.';
  }

  return undefined;
}

function formatPLN(value: number): string {
  return `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
}
